import { CheckCircle2, Users, Shield, Clock } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import busFleet from "@/assets/bus-fleet.jpg";

export function AboutSection() {
  const features = [
    { icon: Shield, title: "Safe Travel", description: "Your safety is our top priority" },
    { icon: Clock, title: "Punctual Service", description: "We respect your time" },
    { icon: Users, title: "Professional Team", description: "Experienced and courteous staff" },
  ];

  return (
    <section id="about" className="section-padding relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Column */}
          <div className="relative animate-fade-in-up">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={busFleet}
                alt="Our bus fleet"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
              
              {/* Floating card */}
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-6 neon-border">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="w-10 h-10 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center"
                      >
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">1000+ Happy Travelers</div>
                    <div className="text-sm text-muted-foreground">Trust us for their journeys</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/20 rounded-2xl blur-2xl" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-2xl" />
          </div>

          {/* Content Column */}
          <div className="animate-fade-in-up animation-delay-200">
            <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              About Us
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {siteConfig.about.title}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: siteConfig.about.description }} />
            <style>{`
              .text-muted-foreground strong {
                font-weight: 600;
                color: inherit;
              }
              @media (prefers-color-scheme: light) {
                .text-muted-foreground strong {
                  color: hsl(var(--foreground));
                }
              }
              @media (prefers-color-scheme: dark) {
                .text-muted-foreground strong {
                  color: hsl(var(--secondary-foreground, 210 20% 90%));
                }
              }
            `}</style>

            {/* Features */}
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {features.map((feature) => (
                <div key={feature.title} className="text-center sm:text-left">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-3">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Why Choose Us */}
            <div className="space-y-3">
              <h3 className="font-display font-semibold text-lg mb-4">Why Choose Us</h3>
              {siteConfig.whyChooseUs.slice(0, 4).map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-primary/5 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground/80 dark:text-secondary-foreground/90" dangerouslySetInnerHTML={{ __html: reason }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
